export interface KeyValuePair {
  id: string;
  key: string;
  value: string;
  values: string[];
}

export type LogicalOperator = 'and' | 'or';

export interface CatalogRuleCondition {
  id: string;
  type: 'condition';
  logicalOperator: LogicalOperator;
  field: string;
  operator: string;
  value: string;
  pairs: KeyValuePair[];
}

export interface CatalogRuleConditionGroup {
  id: string;
  type: 'group';
  logicalOperator: LogicalOperator;
  matchType: 'any' | 'all';
  children: ConditionItem[];
}

export type ConditionItem = CatalogRuleCondition | CatalogRuleConditionGroup;

export const isConditionGroup = (item: ConditionItem): item is CatalogRuleConditionGroup =>
  item.type === 'group';

export interface CatalogRuleAction {
  id: string;
  field: string;
  value: string;
  pairs: KeyValuePair[];
}

export interface CatalogRule {
  id: string;
  enabled: boolean;
  conditions: ConditionItem[];
  actions: CatalogRuleAction[];
}

export const generateId = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 7);

export const createEmptyCondition = (): CatalogRuleCondition => ({
  id: generateId(),
  type: 'condition',
  logicalOperator: 'and',
  field: '',
  operator: '',
  value: '',
  pairs: [],
});

export const createEmptyConditionGroup = (): CatalogRuleConditionGroup => ({
  id: generateId(),
  type: 'group',
  logicalOperator: 'and',
  matchType: 'all',
  children: [createEmptyCondition()],
});

const cloneConditionItem = (item: ConditionItem): ConditionItem => {
  if (isConditionGroup(item)) {
    return {
      ...item,
      id: generateId(),
      children: item.children.map(cloneConditionItem),
    };
  }
  return {
    ...item,
    id: generateId(),
    pairs: item.pairs.map(p => ({ ...p, id: generateId() })),
  };
};

export const deepCloneRule = (rule: CatalogRule): CatalogRule => ({
  id: generateId(),
  enabled: rule.enabled,
  conditions: rule.conditions.map(cloneConditionItem),
  actions: rule.actions.map(a => ({
    ...a,
    id: generateId(),
    pairs: a.pairs.map(p => ({ ...p, id: generateId() })),
  })),
});

export interface FieldOption {
  value: string;
  label: string;
  conditionOnly?: boolean;
}

export interface FieldOptionGroup {
  group: string;
  options: readonly FieldOption[];
}

export const FIELD_GROUPS: readonly FieldOptionGroup[] = [
  {
    group: 'Catalog Fields',
    options: [
      { value: 'parentSku', label: 'Parent SKU', conditionOnly: true },
      { value: 'sku', label: 'SKU', conditionOnly: true },
      { value: 'title', label: 'Title' },
      { value: 'description', label: 'Description' },
      { value: 'checkinEligibility', label: 'Check-In Eligibility' },
      { value: 'resaleEligibility', label: 'Resale Eligibility' },
      { value: 'onlineTradeinEligibility', label: 'Online Trade-In Eligibility' },
      { value: 'instoreTradeinEligibility', label: 'In-Store Trade-In Eligibility' },
      { value: 'repairEligibility', label: 'Repair Eligibility' },
      { value: 'taxCode', label: 'Tax Code' },
      { value: 'rubricCode', label: 'Rubric Code' },
      { value: 'msrpPrice', label: 'MSRP' },
      { value: 'lastKnownRetailPrice', label: 'Last Known Retail Price' },
      { value: 'brand', label: 'Brand' },
      { value: 'department', label: 'Department' },
      { value: 'category', label: 'Category' },
      { value: 'subcategory', label: 'Subcategory' },
      { value: 'seasonCode', label: 'Season Code', conditionOnly: true },
      { value: 'colorCode', label: 'Color Code', conditionOnly: true },
      { value: 'sizeCode', label: 'Size Code', conditionOnly: true },
      { value: 'genderCode', label: 'Gender Code' },
      { value: 'gender', label: 'Gender Display' },
      { value: 'json:family', label: 'Family' },
      { value: 'json:data', label: 'Data' },
      { value: 'json:settings', label: 'Settings' },
    ],
  },
  {
    group: 'Catalog Aliases',
    options: [
      { value: 'partnerParentSku', label: 'Partner Parent SKU', conditionOnly: true },
      { value: 'partnerSku', label: 'Partner SKU', conditionOnly: true },
      { value: 'upc', label: 'UPC', conditionOnly: true },
      { value: 'mpn', label: 'MPN', conditionOnly: true },
      { value: 'gtin', label: 'GTIN', conditionOnly: true },
    ],
  },
];

export const CONDITION_FIELD_GROUPS: FieldOptionGroup[] = FIELD_GROUPS
  .map(g => ({ ...g, options: [...g.options] }));

export const ACTION_FIELD_OPTIONS: FieldOption[] = FIELD_GROUPS
  .flatMap(g => g.options.filter(o => !o.conditionOnly));

export const CONDITION_OPERATOR_OPTIONS = [
  { value: 'is', label: 'is' },
  { value: 'isNot', label: 'is not' },
  { value: 'isOneOf', label: 'is one of' },
  { value: 'contains', label: 'contains' },
  { value: 'doesNotContain', label: 'does not contain' },
  { value: 'startsWith', label: 'starts with' },
  { value: 'endsWith', label: 'ends with' },
  { value: 'isEmpty', label: 'is empty' },
  { value: 'isNotEmpty', label: 'is not empty' },
] as const;

export const NUMERIC_OPERATOR_OPTIONS = [
  { value: 'is', label: 'is' },
  { value: 'isNot', label: 'is not' },
  { value: 'isOneOf', label: 'is one of' },
  { value: 'gt', label: 'is greater than' },
  { value: 'lt', label: 'is less than' },
  { value: 'gte', label: 'is greater than or equal to' },
  { value: 'lte', label: 'is less than or equal to' },
] as const;

export const JSON_OPERATOR_OPTIONS = [
  { value: 'matches', label: 'matches' },
  { value: 'notMatches', label: 'does not match' },
  { value: 'isEmpty', label: 'is empty' },
  { value: 'isNotEmpty', label: 'is not empty' },
] as const;

const NUMERIC_FIELDS = new Set(['msrpPrice', 'lastKnownRetailPrice']);

export const isJsonField = (field: string) => field.startsWith('json:');
export const isFamilyField = (field: string) => field === 'json:family';
export const isNumericField = (field: string) => NUMERIC_FIELDS.has(field);

